import { sameLevels } from 'util/groups/approvalAssignments';
import { RootState } from 'app/store/rootReducer';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { graphqlOperation } from 'aws-amplify';
import { createAsyncThunk, createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  CardGroup,
  CreateCardGroupInput,
  GetMerchantSpendRuleQuery,
  HighnotePaymentCard,
  ListPaidolUserToCardGroupsQuery,
  MerchantSpendRule,
  PaidolUser,
  RemoveCardGroupInput,
  UpdateCardGroupInput,
  UpdateHighnotePaymentCardInput,
  UpdateHighnotePaymentCardMutation,
} from 'API';
// SEE-1232 row b: create/update go to CDK (cardService); the superseded Amplify documents stay in
// graphql_custom/card-groups (SEE-1238 removes them).
// SEE-1232 row e: `listPaidolUserToCardGroups` now reads CDK (cardService). The Amplify document
// stays in graphql_custom, unreferenced; removing superseded documents is SEE-1238's job.
import { getMerchantSpendRule } from 'graphql_custom/card-groups/queries';
import { updateHighnotePaymentCard } from 'graphql/mutations';
import { graphqlRequest } from 'app/services/middleware';
import { cardService, GroupAdminLinksPage } from 'app/services/card.service';
import { userService } from 'app/services/user.service';
// SEE-193: attach-spend-rules writes go to the CDK AppSync API (card.service).
import { AttachSpendRulesToGroupCardsInput, V2ListCardGroupsByPaidolIdPayload } from 'CDK_API';

// The link shape the Amplify read resolved; the CDK read serves the same fields.
type CardGroupAdminLink = NonNullable<
  NonNullable<ListPaidolUserToCardGroupsQuery['listPaidolUserToCardGroups']>['items'][number]
>;

const groupsEntityAdapter = createEntityAdapter<CardGroup>();

const initialState = groupsEntityAdapter.getInitialState({
  loading: true,
  editPending: false,
  nextToken: undefined as string | undefined,
  selectedGroupId: undefined as string | undefined,
  selectedSpendRuleGroup: {} as MerchantSpendRule,
  total: 0,
  allGroups: [] as CardGroup[],
});

export type GroupsState = typeof initialState;

export interface GetGroupsArgs {
  paidolId: string;
  nextToken?: string;
  limit?: number;
  sort?: { field: string; direction: string };
  filter?: {
    and?: Array<{ or: Array<Record<string, unknown>> }>;
  };
}

export type GroupAdminsInput = {
  cardGroupId: string;
  groupAdmins: Partial<PaidolUser>[];
  paidolId: string;
  /** Paidol user id -> the levels they sign off on. One person may hold both; absent means none. */
  approvalLevels?: Record<string, number[]>;
  /** Who may mark this group's transactions reviewed. Empty means every admin. */
  reviewerIds?: string[];
};

export const selectCardGroupsNextToken = (state: RootState) => state.cards?.groups?.nextToken;

export const getGroups = createAsyncThunk(
  'team/groups/getGroups',
  async ({ paidolId, nextToken, limit, sort, filter }: GetGroupsArgs) => {
    const payload = await cardService.v2ListCardGroupsByPaidolId({
      paidolId,
      nextToken,
      limit,
      sort: sort || undefined,
      filter: filter || undefined,
    });
    // Preserve the wrapped shape the extraReducers consume (results.data). Bridge the CDK
    // CardGroup items to the slice's API CardGroup type (create/update paths stay Amplify).
    return {
      v2ListCardGroupsByPaidolId: {
        items: payload.items as unknown as CardGroup[],
        nextToken: payload.nextToken ?? undefined,
        total: payload.total ?? undefined,
      },
    };
  }
);

export const getAllGroups = createAsyncThunk(
  'team/groups/loadAllGroups',
  async (paidolId: string): Promise<CardGroup[]> => {
    let nextToken: string | null | undefined = undefined;
    const allGroups: CardGroup[] = [];

    do {
      const payload: V2ListCardGroupsByPaidolIdPayload = await cardService.v2ListCardGroupsByPaidolId({
        paidolId,
        nextToken: nextToken ?? undefined,
        limit: 100,
      });

      // CDK payload items are the narrower CDK CardGroup; the slice (and its Amplify-backed
      // create/update paths) is typed with the API CardGroup, so bridge at this boundary.
      const items = (payload?.items ?? []) as unknown as CardGroup[];
      allGroups.push(...items.filter((group) => group && !group.deletedAt));

      nextToken = payload?.nextToken;
    } while (nextToken);

    return allGroups;
  }
);

// Fetches all groups for filter options without affecting the displayed filtered groups
export const getAllGroupsNoLimit = createAsyncThunk(
  'team/groups/getAllGroupsNoLimit',
  async (paidolId: string): Promise<CardGroup[]> => {
    let nextToken: string | null | undefined = undefined;
    const allGroups: CardGroup[] = [];

    do {
      const payload: V2ListCardGroupsByPaidolIdPayload = await cardService.v2ListCardGroupsByPaidolId({
        paidolId,
        nextToken: nextToken ?? undefined,
        limit: 100,
      });

      // CDK payload items are the narrower CDK CardGroup; the slice (and its Amplify-backed
      // create/update paths) is typed with the API CardGroup, so bridge at this boundary.
      const items = (payload?.items ?? []) as unknown as CardGroup[];
      allGroups.push(...items.filter((group) => group && !group.deletedAt));

      nextToken = payload?.nextToken;
    } while (nextToken);

    return allGroups;
  }
);

export const newGroup = createAsyncThunk('team/groups/newGroup', async (input: CreateCardGroupInput) => {
  // SEE-1232 row b: CDK derives authPaidolId server-side, so it is not sent.
  const { authPaidolId: _derived, ...cdkInput } = input;
  return cardService.createCardGroup<CardGroup>(cdkInput as unknown as Record<string, unknown>);
});

export const removeGroup = createAsyncThunk(
  'team/groups/removeGroup',
  async (input: RemoveCardGroupInput) => {
    // Return value is unused — the fulfilled reducer removes by action.meta.arg.cardGroupId.
    return cardService.removeCardGroup({ input });
  }
);

export const updateGroup = createAsyncThunk(
  'team/groups/updateGroup',
  async (input: UpdateCardGroupInput) => {
    // SEE-1232 row b: transport flip only — same input, re-keyed CardGroup result.
    return cardService.updateCardGroup<CardGroup>(input as unknown as Record<string, unknown>);
  }
);

export const updateGroupAdmins = createAsyncThunk(
  'team/groups/updateGroupAdmins',
  async (input: GroupAdminsInput, { getState }) => {
    if (!input.cardGroupId || !input.paidolId) {
      throw new Error('cardGroupId and paidolId are required');
    }

    // Get current admins (handle pagination to get all records)
    const existingAdmins = [];
    let nextToken: string | null = null;

    do {
      // SEE-1232 row e: transport flip only — same page size, same pagination loop.
      const page: GroupAdminLinksPage<CardGroupAdminLink> = await cardService.listPaidolUserToCardGroups({
        paidolId: input.paidolId,
        cardGroupId: input.cardGroupId,
        limit: 1000,
        nextToken,
      });
      existingAdmins.push(...page.items);
      nextToken = page.nextToken;
    } while (nextToken);
    const existingAdminIds = new Set(existingAdmins.map((admin) => admin?.paidolUserId).filter(Boolean));
    const newAdminIds = new Set(input.groupAdmins.map((admin) => admin.id).filter(Boolean));

    // Find admins to add (new ones)
    const adminsToAdd = input.groupAdmins.filter((admin) => admin.id && !existingAdminIds.has(admin.id));

    // Find admins to remove (existing ones not in new list OR duplicates)
    const seenUserIds = new Set<string>();
    const adminsToRemove = existingAdmins.filter((admin) => {
      if (!admin?.id || !admin?.paidolUserId) return false;

      // Remove if user is not in the new list
      if (!newAdminIds.has(admin.paidolUserId)) {
        return true;
      }

      // Remove if this is a duplicate (not the first occurrence)
      if (seenUserIds.has(admin.paidolUserId)) {
        return true;
      }

      seenUserIds.add(admin.paidolUserId);
      return false; // First occurrence, keep it
    });

    // Assignments ride along only when the editor showed the pickers — the
    // backend treats a present set as authoritative for every member.
    const sendAssignments = input.approvalLevels !== undefined || input.reviewerIds !== undefined;
    // A level change lands on a row that already exists, so it is neither an add
    // nor a remove and has to be diffed separately. Only a save carrying
    // assignments can change one, so a picker-less save keeps the short-circuit.
    const removedIds = new Set(adminsToRemove.map((admin) => admin?.id));
    const levelChanges = !sendAssignments
      ? []
      : existingAdmins.filter((admin) => {
          if (!admin?.id || !admin?.paidolUserId || removedIds.has(admin.id)) return false;
          return (
            !sameLevels(admin.approvalLevels, input.approvalLevels?.[admin.paidolUserId]) ||
            (admin.isReviewer ?? false) !== (input.reviewerIds ?? []).includes(admin.paidolUserId)
          );
        });

    // Only call the backend when something changes.
    if (adminsToAdd.length === 0 && adminsToRemove.length === 0 && levelChanges.length === 0) {
      const state = getState() as RootState;
      const currentGroup = selectGroupById(state, input.cardGroupId);
      return currentGroup ?? null;
    }

    // SEE-1314: the join-table create/delete mutations are gone; the admin-only
    // backend mutation sets the full admin list (it diffs against the stored rows).
    // De-dupe: the mutation writes one row per id, and duplicate members
    // occur in real data (see the seenUserIds cleanup above).
    const paidolUserIds = [
      ...new Set(input.groupAdmins.map((admin) => admin.id).filter((id): id is string => Boolean(id))),
    ];
    await userService.adminSetGroupAdmins({
      paidolId: input.paidolId,
      groupType: 'CARD',
      groupId: input.cardGroupId,
      paidolUserIds,
      ...(sendAssignments && {
        assignments: paidolUserIds.map((id) => ({
          paidolUserId: id,
          approvalLevels: input.approvalLevels?.[id] ?? [],
          isReviewer: (input.reviewerIds ?? []).includes(id),
        })),
      }),
    });

    // Return updated list (handle pagination to get all records)
    const finalAdmins = [];
    let finalNextToken: string | null = null;

    do {
      const page: GroupAdminLinksPage<CardGroupAdminLink> = await cardService.listPaidolUserToCardGroups({
        paidolId: input.paidolId,
        cardGroupId: input.cardGroupId,
        limit: 1000,
        nextToken: finalNextToken,
      });
      finalAdmins.push(...page.items);
      finalNextToken = page.nextToken;
    } while (finalNextToken);

    // Get current group from state and merge with updated admins
    const state = getState() as RootState;
    const currentGroup = selectGroupById(state, input.cardGroupId);

    if (currentGroup) {
      return {
        ...currentGroup,
        groupAdmins: {
          __typename: 'ModelPaidolUserToCardGroupConnection' as const,
          items: finalAdmins as any,
          nextToken: null,
        },
      };
    }

    return null;
  }
);

export const updateHNPaymentCard = createAsyncThunk(
  'team/groups/updateHighnotePaymentCard',
  async (input: UpdateHighnotePaymentCardInput, { dispatch }) => {
    return (
      graphqlRequest(graphqlOperation(updateHighnotePaymentCard, { input }), dispatch) as Promise<
        GraphQLResult<UpdateHighnotePaymentCardMutation>
      >
    ).then((results) => results.data?.updateHighnotePaymentCard as HighnotePaymentCard | undefined);
  }
);

export const attachSpendRules = createAsyncThunk(
  'team/groups/attachSpendRulesToGroupCards',
  async (input: AttachSpendRulesToGroupCardsInput) => {
    // SEE-193: served by the CDK attach-spend-rules-to-group-cards Lambda.
    return cardService.attachSpendRulesToGroupCards(input);
  }
);

export const getSpendRuleGroup = createAsyncThunk(
  'team/groups/getMerchantSpendRule',
  async (merchantSpendRuleId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(getMerchantSpendRule, { id: merchantSpendRuleId }),
        dispatch
      ) as Promise<GraphQLResult<GetMerchantSpendRuleQuery>>
    ).then((result) => result.data?.getMerchantSpendRule as MerchantSpendRule);
  }
);

const groupsSlice = createSlice({
  name: 'team/groups',
  initialState,
  reducers: {
    resetGroupsSlice: () => initialState,
    clearGroups: () => {
      return initialState;
    },
    setSelectedGroupId: (state, action) => {
      state.selectedGroupId = action.payload;
    },
    resetSelectedGroupId: (state) => {
      state.selectedGroupId = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGroups.fulfilled, (state, action) => {
      if (action.payload?.v2ListCardGroupsByPaidolId) {
        groupsEntityAdapter.upsertMany(state, action.payload?.v2ListCardGroupsByPaidolId?.items);
      }
      state.nextToken = action.payload?.v2ListCardGroupsByPaidolId?.nextToken ?? undefined;
      state.total = action.payload?.v2ListCardGroupsByPaidolId?.total ?? 0;
      state.loading = false;
    });

    builder.addCase(attachSpendRules.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(updateGroupAdmins.fulfilled, (state, action) => {
      if (action.payload) {
        groupsEntityAdapter.upsertOne(state, action.payload);
      }
      state.editPending = false;
    });

    builder.addCase(updateHNPaymentCard.fulfilled, (state) => {
      state.editPending = false;
    });

    builder.addCase(getSpendRuleGroup.fulfilled, (state, action) => {
      state.selectedSpendRuleGroup = action.payload;
      state.loading = false;
    });

    builder.addCase(removeGroup.fulfilled, (state, action) => {
      // Extract cardGroupId from the original request
      const cardGroupId = (action.meta.arg as RemoveCardGroupInput).cardGroupId;
      if (cardGroupId) {
        groupsEntityAdapter.removeOne(state, cardGroupId);
      }
      state.loading = false;
    });

    builder.addCase(getAllGroups.fulfilled, (state, action) => {
      groupsEntityAdapter.setAll(state, action.payload);
      state.loading = false;
    });

    builder.addCase(getAllGroupsNoLimit.fulfilled, (state, action) => {
      state.allGroups = action.payload || [];
    });

    builder.addMatcher(isAnyOf(newGroup.pending, updateGroup.pending), (state) => {
      state.editPending = true;
    });

    builder.addMatcher(isAnyOf(newGroup.rejected, updateGroup.rejected), (state) => {
      state.editPending = false;
    });

    builder.addMatcher(isAnyOf(newGroup.fulfilled, updateGroup.fulfilled), (state, action) => {
      if (action.payload) {
        groupsEntityAdapter.upsertOne(state, action.payload);
      }
      state.editPending = false;
    });

    builder.addMatcher(
      isAnyOf(
        getAllGroups.pending,
        removeGroup.pending,
        getSpendRuleGroup.pending,
        updateHNPaymentCard.pending,
        getGroups.pending,
        attachSpendRules.pending
      ),
      (state) => {
        state.loading = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getAllGroups.rejected,
        removeGroup.rejected,
        getSpendRuleGroup.rejected,
        updateHNPaymentCard.rejected,
        getGroups.rejected,
        attachSpendRules.rejected
      ),
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const { resetGroupsSlice, clearGroups, setSelectedGroupId, resetSelectedGroupId } =
  groupsSlice.actions;

export const selectGroupsSlice = (state: RootState) => state.cards?.groups ?? initialState;

export const { selectAll: selectAllGroups, selectById: selectGroupById } =
  groupsEntityAdapter.getSelectors(selectGroupsSlice);

export const selectSelectedGroup = (state: RootState) => {
  if (state.cards?.groups.selectedGroupId) {
    return selectGroupById(state, state.cards?.groups.selectedGroupId);
  }

  return undefined;
};

export default groupsSlice.reducer;
